// Canonical intent vocabulary — the single source of truth that keeps the
// intent classifier and the flow-trigger authoring in lockstep.
//
// WHY THIS EXISTS. A Skill flow runs only when the classifier's output intent
// exactly equals the flow's `trigger.intent` (see engine `dispatch.ts`). For a
// long time there were two independent intent lists: the per-vertical enum the
// classifier could emit (`classification-schemas.ts`) and the free-text /
// seed-authored intents the flows triggered on. They drifted — e.g. three of
// the four seeded healthcare templates triggered on intents the classifier
// could never produce (`cancel_appointment`, `patient_intake`, `quote_request`)
// so they could never fire. This module is the one list both sides consume, so
// that drift is structurally impossible: an intent a flow can pick is, by
// construction, an intent the classifier knows how to emit.
//
// Pure data — no Node deps — so the web flow wizard can import it via the
// `@vysto/shared/intents` subpath without dragging the server-only barrel
// (jsonwebtoken / pino) into the browser bundle.

/** The verticals that have a curated intent vocabulary. `generic` is the
 *  fallback for verticals without a bespoke list yet. Mirrors the engine's
 *  `Vertical` union plus `generic`; kept as a string-keyed lookup so a new
 *  vertical doesn't force a change here. */
export type IntentVerticalKey =
  | 'healthcare'
  | 'casas-de-empeno'
  | 'automotive-new'
  | 'automotive-used'
  | 'doctor'
  | 'generic'

export interface IntentDef {
  /** Stable machine id. The classifier emits this; a flow trigger matches it.
   *  Never change a shipped id without a migration of the flows/templates that
   *  trigger on it. */
  id: string
  /** Plain-language label for the flow wizard ("¿Qué quiere lograr el cliente?").
   *  Spanish (MX), customer-intent phrasing. */
  label: string
  /** One line used both in the classifier's system prompt (so it learns to emit
   *  this intent) and as the wizard's helper text. Keep it concrete. */
  description: string
  /** Whether a flow may attach to this intent. Classifier-internal intents
   *  (`small_talk`, `unknown`) are emitted but make no sense as flow triggers,
   *  so they're hidden from the authoring picker. Defaults to true. */
  triggerable?: boolean
}

// ─── Healthcare ─────────────────────────────────────────────────────────────
// Reconciled with the seeded healthcare skill templates: `appointment`,
// `cancel_appointment`, `patient_intake` are all now first-class, and the
// "Cotizar estudio" template folds into the broader `pricing_question` (a
// separate near-identical `quote_request` only split the classifier's
// confidence for no product gain).

export const HEALTHCARE_INTENTS: readonly IntentDef[] = [
  {
    id: 'appointment',
    label: 'Agendar o reagendar una cita',
    description: 'Quiere reservar, reagendar o preguntar por disponibilidad de una cita médica.',
  },
  {
    id: 'cancel_appointment',
    label: 'Cancelar una cita',
    description: 'Quiere cancelar una cita que ya tenía agendada.',
  },
  {
    id: 'pricing_question',
    label: 'Costos o cotizaciones',
    description:
      'Pregunta por el costo de un servicio o estudio, una cotización, o cobertura de seguro y formas de pago.',
  },
  {
    id: 'service_info',
    label: 'Qué servicios o especialidades hay',
    description: 'Pregunta qué especialidades, estudios o servicios ofrece el hospital.',
  },
  {
    id: 'directions',
    label: 'Dirección, horario o cómo llegar',
    description: 'Pregunta por la dirección, el horario de atención, el teléfono o cómo llegar.',
  },
  {
    id: 'staff_info',
    label: 'Información de un médico',
    description: 'Pregunta por un médico o especialista en particular y su disponibilidad.',
  },
  {
    id: 'patient_intake',
    label: 'Dejar sus datos / registrarse',
    description: 'Quiere dejar su nombre, teléfono y motivo de consulta para que lo contacten.',
  },
  {
    id: 'medical_question',
    label: 'Pregunta clínica',
    description: 'Pregunta clínica o de salud que normalmente necesita a una persona.',
  },
  {
    id: 'complaint',
    label: 'Queja o reclamo',
    description: 'Una queja o reclamo que requiere escalar con una persona.',
  },
  {
    id: 'small_talk',
    label: 'Saludo o charla casual',
    description: 'Saludo, despedida, agradecimiento o charla fuera de tema.',
    triggerable: false,
  },
  {
    id: 'unknown',
    label: 'Sin clasificar',
    description: 'Nada de lo anterior aplica claramente.',
    triggerable: false,
  },
]

// ─── Generic (verticals without a bespoke list yet) ─────────────────────────

export const GENERIC_INTENTS: readonly IntentDef[] = [
  {
    id: 'pricing_question',
    label: 'Costos o cotizaciones',
    description: 'Pregunta por costos, cotizaciones o formas de pago.',
  },
  {
    id: 'service_info',
    label: 'Qué servicios o productos hay',
    description: 'Pregunta qué servicios o productos se ofrecen.',
  },
  {
    id: 'directions',
    label: 'Dirección, horario o contacto',
    description: 'Pregunta por la dirección, el horario o cómo contactar.',
  },
  {
    id: 'complaint',
    label: 'Queja o reclamo',
    description: 'Una queja o reclamo que requiere escalar con una persona.',
  },
  {
    id: 'small_talk',
    label: 'Saludo o charla casual',
    description: 'Saludo, despedida, agradecimiento o charla fuera de tema.',
    triggerable: false,
  },
  {
    id: 'unknown',
    label: 'Sin clasificar',
    description: 'Nada de lo anterior aplica claramente.',
    triggerable: false,
  },
]

const REGISTRY: Record<IntentVerticalKey, readonly IntentDef[]> = {
  healthcare: HEALTHCARE_INTENTS,
  // A solo doctor is healthcare — same intents (mirrors the engine classifier).
  doctor: HEALTHCARE_INTENTS,
  'casas-de-empeno': GENERIC_INTENTS,
  'automotive-new': GENERIC_INTENTS,
  'automotive-used': GENERIC_INTENTS,
  generic: GENERIC_INTENTS,
}

/** All intents the classifier may emit for a vertical (including the
 *  classifier-internal `small_talk` / `unknown`). Unknown verticals fall back
 *  to the generic list, matching the classifier registry's own fallback. */
export function intentsForVertical(vertical: string): readonly IntentDef[] {
  return REGISTRY[vertical as IntentVerticalKey] ?? GENERIC_INTENTS
}

/** Just the intents a flow may attach to — what the authoring wizard offers. */
export function triggerableIntents(vertical: string): readonly IntentDef[] {
  return intentsForVertical(vertical).filter((i) => i.triggerable !== false)
}
